import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type TipoEmailCliente = {
  codigo: string;
  rotulo: string;
  dica: string;
  assunto: string | null;
  exigePfy?: boolean;
  texto: string;
};

export type ContatoSugerido = { nome: string; email: string; papel: string | null; padrao: boolean };

export type EmailClienteContexto = {
  cliente: { id: string; cnpj: string; cnpj_formatado: string; razao: string | null };
  pedido: { id: string; numero: string | null } | null;
  fila: string | null;
  tipo_sugerido: string;
  tipos: TipoEmailCliente[];
  defesas: Array<{ pfy: string; em: string; motivo: string | null; cotacao: string | null }>;
  para_sugeridos: ContatoSugerido[];
  copia_sugeridos: ContatoSugerido[];
  supervisores?: Array<{ nome: string; email: string; papel: string | null }>;
  remetente: { nome: string; funcao: string | null; foto_url: string | null; caixa: string };
};

export type EmailClienteMensagem = {
  id: string;
  direcao: 'entrada' | 'saida';
  de_nome: string | null;
  de_email: string | null;
  para: Array<{ nome?: string | null; email?: string | null }> | null;
  cc: Array<{ nome?: string | null; email?: string | null }> | null;
  assunto: string | null;
  corpo_texto: string | null;
  quando: string | null;
  cnpj: string | null;
  pedido_id: string | null;
  qtd_anexos: number | null;
  caixa: string | null;
};

const base = () => (import.meta.env.VITE_SUPABASE_URL as string) ?? 'https://beta.crm-api.primetecia.com.br/api';

async function chamar<T>(caminho: string, init?: RequestInit): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sessão expirada. Faça login novamente.');
  const resp = await fetch(`${base()}${caminho}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}`, ...(init?.headers || {}) },
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(json?.error || 'Não foi possível falar com o servidor.');
  return json.data as T;
}

export function useEmailClienteContexto(alvo: { pedidoId?: string | null; cnpj?: string | null }, enabled: boolean) {
  const qs = new URLSearchParams();
  if (alvo.pedidoId) qs.set('pedido_id', alvo.pedidoId);
  if (alvo.cnpj) qs.set('cnpj', alvo.cnpj);
  return useQuery({
    queryKey: ['email-cliente', 'contexto', alvo.pedidoId ?? null, alvo.cnpj ?? null],
    enabled: enabled && (!!alvo.pedidoId || !!alvo.cnpj),
    staleTime: 60_000,
    queryFn: () => chamar<EmailClienteContexto>(`/v1/email-cliente/contexto?${qs.toString()}`),
  });
}

export function useEmailClienteMensagem(id: string | null) {
  return useQuery({
    queryKey: ['email-cliente', 'mensagem', id],
    enabled: !!id,
    staleTime: 300_000,
    queryFn: () => chamar<EmailClienteMensagem>(`/v1/email-cliente/mensagem/${id}`),
  });
}

export type EnviarEmailCliente = {
  pedido_id?: string | null;
  cnpj?: string | null;
  tipo?: string | null;
  pfy?: string | null;
  complemento?: string | null;
  para: string[];
  cc: string[];
  texto: string;
  responder_a?: string | null;
};

export function useEnviarEmailCliente() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: EnviarEmailCliente) =>
      chamar<{ email_id: string | null; assunto: string; para: string }>('/v1/email-cliente/enviar', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timeline-unificada'] });
    },
  });
}
